import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useSendMetrics } from '../../../hooks/use-send-metrics';
import { messages } from './messages';
import { PurchaseOrderProvider } from './purchase-order-context';
import { PurchaseOrderDrawer } from './purchase-order-drawer/purchase-order-drawer';
import style from '../../../components/grid-header/reusable-grid-button.module.less';
import { PURCHASE_ORDER_ACTION_CREATE_NEW_CLICKED } from '../../../constants/analytics-events';
import { useModal } from '../../../hooks/use-modal';

/**
 * @module AddPurchaseOrderButton
 * @description A component that renders a button to add a new purchase order.
 * When clicked, it opens a drawer with the purchase order form.
 * The component uses the useModal hook to manage the drawer's open/close state.
 * When the drawer is open, it renders the CreatePurchaseOrderDrawer component wrapped
 * in a PurchaseOrderProvider to provide context for the purchase order form.
 * @param {object} props - Component props
 * @param {(() => Promise<void>)} [props.refreshPurchaseOrders] - Callback function to refresh purchase orders after changes
 * @param {import("./hooks/useInitializePurchaseOrderWithProducts").InitializedPurchaseOrder} [props.initializedPurchaseOrder] - Optional initial data for the purchase order, including supplier ID, selected products with quantities, SKUs, costs, and warehouse ID
 * @param {boolean} [props.disabled] - Optional flag to disable the button
 * @param {string} [props.text] - Optional text to display on the button. If not provided, defaults to a formatted message.
 * @param {string} [props.theme] - Optional theme for the button
 * @returns {JSX.Element} A button that opens the purchase order drawer when clicked
 * @example
 * <AddPurchaseOrderButton refreshPurchaseOrders={() => fetchPurchaseOrders()} />
 */
export const AddPurchaseOrderButton = ({ refreshPurchaseOrders, initializedPurchaseOrder, disabled, text, theme }) => {
  const { open: openPurchaseOrderDrawer, close: closePurchaseOrderDrawer, isOpen: isPurchaseOrderDrawerOpen } = useModal();
  const buttonText = text || <FormattedMessage {...messages.addPurchaseOrderButton} />;
  const buttonTheme = theme || THEME_CONSTANTS.primary;
  const trackMetrics = useSendMetrics();

  const createPurchaseOrderClicked = useCallback(() => {
    openPurchaseOrderDrawer();
    trackMetrics(PURCHASE_ORDER_ACTION_CREATE_NEW_CLICKED, {
      page: window.location.pathname,
    });
  }, [openPurchaseOrderDrawer, trackMetrics]);

  return (
    <>
      <Button className={style['reusable-button']} disabled={disabled} onClick={createPurchaseOrderClicked} theme={buttonTheme}>
        {buttonText}
      </Button>
      {isPurchaseOrderDrawerOpen ? (
        <PurchaseOrderProvider
          initializedPurchaseOrder={initializedPurchaseOrder}
          onClose={closePurchaseOrderDrawer}
          refreshPurchaseOrders={refreshPurchaseOrders}
        >
          <PurchaseOrderDrawer />
        </PurchaseOrderProvider>
      ) : null}
    </>
  );
};
